import { Box, Typography } from '@mui/material';
import { ReactComponent as ChangeStateIcon } from 'assets/arrow-left-right.svg';
import { ReactComponent as ArrowRightIcon } from 'assets/arrow-next.svg';
import { ReactComponent as DeleteIcon } from 'assets/delete-red.svg';
import { ReactComponent as EditIcon } from 'assets/edit.svg';
import { ReactComponent as ViewIcon } from 'assets/view.svg';
import CustomDeleteModal from 'components/shared/CustomDeleteModal/CustomDeleteModal';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import { CompleteOperationType, StartOperationType } from 'model/alerts.model';
import { MouseEvent, useEffect, useReducer, useState } from 'react';
import { Link } from 'react-router-dom';
import { modalReducer } from 'reducers/modalReducer';
import RoutePaths from 'routes/RoutePaths';
import { DealsService } from 'service/deals.service';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchStages } from 'store/slices/dealsSlice';
import zeplinColor from 'theme/Color';

type DealSetting = {
  label: string;
  type: string;
  icon: JSX.Element;
  hasSubmenu: boolean;
  routePath?: (id: string) => string;
  dealStageNames?: string[];
  submenuIcon?: JSX.Element;
};

const DEAL_SETTINGS_DATA: DealSetting[] = [
  {
    label: 'Change stage',
    type: 'button',
    icon: <ChangeStateIcon />,
    hasSubmenu: true,
    dealStageNames: [],
    submenuIcon: <ArrowRightIcon />,
  },
  {
    label: 'View details',
    type: 'link',
    icon: <ViewIcon />,
    hasSubmenu: false,
    routePath: (id) => `${RoutePaths.Deals}/${id}`,
  },
  {
    label: 'Edit deal',
    type: 'link',
    icon: <EditIcon />,
    hasSubmenu: false,
    routePath: (id) => `${RoutePaths.Deals}/${id}/edit`,
  },
  {
    label: 'Delete',
    type: 'button',
    icon: <DeleteIcon />,
    hasSubmenu: false,
  },
];

export const DealsSettings = ({
  anchorElement,
  onClose,
  startOperation,
  completeOperation,
  deal,
  onDealDeleted,
  onStageChange,
}: {
  anchorElement: SVGSVGElement | null;
  onClose: () => void;
  startOperation: StartOperationType;
  completeOperation: CompleteOperationType;
  deal?: any;
  onDealDeleted?: () => void;
  onStageChange?: () => void;
}) => {
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    delete: false,
  });
  const stages = useAppSelector((state) => state.deals.stages);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchStages());
  }, [dispatch]);

  const onDeleteClick = async () => {
    if (!deal?.id && !deal?.dealId) {
      completeOperation('delete', 'Deal ID not found', true);
      return;
    }

    try {
      startOperation('delete', 'Deleting deal...');
      onClose();
      modalDispatch({ type: 'CLOSE', payload: 'delete' });
      const dealId = deal.id || deal.dealId;
      await DealsService.deleteDeal(dealId);

      if (onDealDeleted) {
        onDealDeleted();
      }

      completeOperation('delete', 'Deal deleted successfully');
    } catch (error: any) {
      completeOperation('delete', error?.response?.data?.message || 'Failed to delete deal', true);
    }
  };

  const [submenuAnchor, setSubmenuAnchor] = useState<HTMLElement | null>(null);

  const handleStageClick = (e: MouseEvent) => {
    setSubmenuAnchor(e.currentTarget as HTMLElement);
  };

  const handleStageSubMenuClose = () => {
    setSubmenuAnchor(null);
  };

  const handleStageChange = async (newStage: string) => {
    if (!deal?.id && !deal?.dealId) {
      completeOperation('update', 'Deal ID not found', true);
      return;
    }

    const dealId = deal.dealId;

    try {
      startOperation('update', 'Updating deal stage...');
      onClose();

      await DealsService.updateDeal(dealId, { stageName: newStage });

      if (onStageChange) {
        onStageChange();
      }

      if (onDealDeleted) {
        onDealDeleted();
      }

      completeOperation('update', 'Deal stage updated successfully');
    } catch (error: any) {
      completeOperation(
        'update',
        error?.response?.data?.message || 'Failed to update deal stage',
        true,
      );
    }
  };

  return (
    <CustomPopOver
      onClick={(e: MouseEvent<SVGSVGElement>) => {
        e.stopPropagation();
      }}
      setAnchorEl={onClose}
      anchorEl={anchorElement}
      id={'simple-popover'}
      sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px',
        }}
      >
        {DEAL_SETTINGS_DATA.map(({ type, label, icon, routePath, submenuIcon }: DealSetting) => {
          if (type === 'link' && routePath) {
            return (
              <Box
                to={routePath(deal?.dealId || deal?.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9px',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  bgcolor: 'transparent',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                }}
                key={label}
                component={Link}
              >
                {icon}
                <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                  {label}
                </Typography>
              </Box>
            );
          } else {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9px',
                  cursor: 'pointer',
                  bgcolor: 'transparent',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                }}
                key={label}
                component={'button'}
                onClick={
                  label.includes('stage')
                    ? handleStageClick
                    : label === 'Delete'
                      ? () => modalDispatch({ type: 'OPEN', payload: 'delete' })
                      : undefined
                }
              >
                {icon}
                <Typography
                  variant='responsiveBody3'
                  color={label === 'Delete' ? zeplinColor.Error : zeplinColor.BasicBlack}
                >
                  {label}
                </Typography>
                {submenuIcon}
                {submenuAnchor && stages.length && (
                  <StageSubMenu
                    options={stages}
                    onClose={handleStageSubMenuClose}
                    anchorElement={submenuAnchor}
                    onStageSelect={handleStageChange}
                  />
                )}
              </Box>
            );
          }
        })}
      </Box>
      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={onDeleteClick}
        title='Delete Deal'
        description={`You’re about to delete the deal “${deal?.name || deal?.title || 'this deal'}” This action cannot be undone. Are you sure you want to delete?`}
      />
    </CustomPopOver>
  );
};

const StageSubMenu = ({
  options,
  onClose,
  anchorElement,
  onStageSelect,
}: {
  options: string[];
  onClose: () => void;
  anchorElement: HTMLElement;
  onStageSelect?: (stage: string) => void;
}) => {
  return (
    <CustomPopOver
      onClick={(e) => {
        e.stopPropagation();
      }}
      setAnchorEl={onClose}
      anchorEl={anchorElement}
      id={'simple-popover'}
      sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
      vertical='right'
      horizontal='right'
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px',
        }}
      >
        {options.map((option: string) => (
          <Box
            key={option}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              cursor: 'pointer',
              bgcolor: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
            }}
            onClick={() => {
              if (onStageSelect) {
                onStageSelect(option);
              }
              onClose();
            }}
            component={'button'}
          >
            <Typography variant='responsiveBody3'>{option}</Typography>
          </Box>
        ))}
      </Box>
    </CustomPopOver>
  );
};
